import { useGetAllQuestionsV2ApplyQuery } from '@core/app/api/applyJobV2Api'
import {
    ApplyV2CustomFieldTypeConst,
    type ApplyV2Question,
    type ApplyV2StarRatingQuestion,
} from '@core/app/types/applyV2Type'
import ApplyV2AttachmentsUploader from '@core/components/ApplyDialogV2/ApplyV2AttachmentsUploader'
import ApplyV2DataRow from '@core/components/ApplyDialogV2/ApplyV2DataRow'
import ApplyV2Picture from '@core/components/ApplyDialogV2/ApplyV2Picture'
import FileUploaderV2List from '@core/components/ApplyDialogV2/FileUploaderV2List'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import getCurrencyById from '@core/utils/helpers/getCurrencyById'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import useFormattedDatesPeriod from '@core/utils/hooks/useFormattedDatesPeriod'
import React, { useEffect } from 'react'
import './ApplyV2ReviewApplyQuestions.scss'

function sortQuestionsRecursively(questions: ApplyV2Question[]): ApplyV2Question[] {
    return [...questions]
        .sort((a, b) => a.orderId - b.orderId)
        .map((question) => ({
            ...question,
            children: question.children ? sortQuestionsRecursively(question.children) : [],
        }))
        .filter((q) => q.visible)
}

const baseClass = 'ApplyV2ReviewApplyQuestions'

function ApplyV2ReviewApplyQuestions() {
    const { storageData } = useApplyV2Context()

    const { data, isLoading, isFetching, refetch } = useGetAllQuestionsV2ApplyQuery(
        { uid: storageData?.postInitUid || '' },
        { skip: !storageData?.postInitUid }
    )

    const questions = data?.data?.questions ? sortQuestionsRecursively([...data.data.questions]) : []

    // const questions = [...(data?.data?.questions || [])].sort((a, b) => a.orderId - b.orderId)
    console.log(questions)

    // useEffect(() => {
    //     if (refetch) refetch()
    // })

    if (isLoading) {
        return (
            <div className={baseClass}>
                <ApplySkeleton isLoading={isLoading} />
                <ApplySkeleton isLoading={isLoading} />
                <ApplySkeleton isLoading={isLoading} />
            </div>
        )
    }
    return (
        <div className={baseClass}>
            <ReviewQuestionsList list={questions} />
        </div>
    )
}

function ReviewQuestionsList({ list }: { list: ApplyV2Question[] | null | undefined }) {
    // const questions = (list ? [...list] : []).sort((a, b) => a.orderId - b.orderId)

    const questions = list ? sortQuestionsRecursively([...list]) : []

    if (questions.length === 0) {
        return null
    }

    return (
        <div className={`${baseClass}__list`}>
            {questions.map((question, index) => {
                return <ReviewQuestionItem key={question.questionId} question={question} />
            })}
        </div>
    )
}

function ReviewQuestionItem({ question }: { question: ApplyV2Question }) {
    const { getTitle } = useApplyV2NavTranslations()
    const { formatDateRange } = useFormattedDatesPeriod()

    const renderAnswer = () => {
        const hasValue = (value: any): boolean => {
            if (value == null) return false
            if (typeof value === 'string') return value.trim() !== ''
            if (typeof value === 'number') return true
            if (typeof value === 'boolean') return true
            if (Array.isArray(value)) return value.length > 0
            if (typeof value === 'object') return Object.keys(value).length > 0
            return false
        }

        switch (question.type) {
            case 'starRating':
                return (
                    question.visible &&
                    question.answer?.value &&
                    question.answer?.value > 0 && (
                        <ApplyV2DataRow title={question.name}>
                            <span className={`${baseClass}__rating`}>
                                <span className={`${baseClass}__rating__star`}>★</span>
                                {question.answer?.value || ''}
                            </span>
                        </ApplyV2DataRow>
                    )
                )

            case 'attachment':
                return (
                    // (question?.answer?.value ?? []).length > 0 && (
                    (question?.answer?.value ?? []).length > 0 &&
                    question.visible && (
                        <ApplyV2DataRow title={question.name}>
                            <ApplyV2AttachmentsUploader isShowInput={false} files={question?.answer?.value ?? []} />
                        </ApplyV2DataRow>
                    )
                )

            case 'text':
            case 'longText':
            case 'number':
                return (
                    question.answer?.value &&
                    question.visible && <ApplyV2DataRow title={question.name}>{question.answer?.value}</ApplyV2DataRow>
                )

            case 'yesNo':
            case 'qualifying':
                const selectedValue = (question.options ?? []).find((opt) => opt.id === question.answer?.value)
                return (
                    selectedValue &&
                    question.visible && (
                        <ApplyV2DataRow title={question.name}>
                            {selectedValue.name.toLowerCase() === 'yes'
                                ? getTitle('128', 'Yes')
                                : getTitle('129', 'No')}
                        </ApplyV2DataRow>
                    )
                )

            case 'date':
                return (
                    question.answer?.value &&
                    question.visible && (
                        <ApplyV2DataRow title={question.name}>
                            {formatDateRange(question.answer?.value, null, {
                                isShowPresent: false,
                                dateFormat: 'D MMM YYYY',
                            })}
                        </ApplyV2DataRow>
                    )
                )

            case 'dateRange':
                return (
                    (question.answer?.value?.[0] || question.answer?.value?.[1]) &&
                    question.visible && (
                        <ApplyV2DataRow title={question.name}>
                            {formatDateRange(question.answer?.value?.[0] ?? null, question.answer?.value?.[1] ?? null, {
                                isShowPresent: true,
                                dateFormat: 'D MMM YYYY',
                            })}
                        </ApplyV2DataRow>
                    )
                )

            case 'singleSelect':
                return (
                    // question.answer && (
                    question.answer?.value &&
                    question.visible && (
                        <ApplyV2DataRow title={question.name}>
                            {(question.options ?? []).find(({ id }) => id === question.answer?.value)?.name}
                        </ApplyV2DataRow>
                    )
                )
            case 'multiSelect':
                const selectedValues = (question.answer?.value ?? []) as number[]
                const options = question.options ?? []

                const selectedOptions = selectedValues.map((id) => {
                    return options.find((option) => option.id === id)?.name ?? ''
                })
                return (
                    selectedValues.length > 0 &&
                    question.visible && (
                        <ApplyV2DataRow title={question.name}>{selectedOptions.join(', ')}</ApplyV2DataRow>
                    )
                )
            case 'customField':
                switch (question.customField.typeId) {
                    case ApplyV2CustomFieldTypeConst.SingleSelect:
                        return (
                            question.answer &&
                            question.visible && (
                                <ApplyV2DataRow title={question.name}>
                                    {(question.options ?? []).find(({ id }) => id === question.answer?.value)?.name}
                                </ApplyV2DataRow>
                            )
                        )

                    case ApplyV2CustomFieldTypeConst.MultiSelect: {
                        const selectedValues = (question.answer?.value ?? []) as number[]
                        const options = question.options ?? []
                        const selectedOptions = selectedValues.map(
                            (id) => options.find((option) => option.id === id)?.name ?? ''
                        )
                        return (
                            selectedValues.length > 0 &&
                            question.visible && (
                                <ApplyV2DataRow title={question.name}>{selectedOptions.join(', ')}</ApplyV2DataRow>
                            )
                        )
                    }

                    case ApplyV2CustomFieldTypeConst.Text:
                        return (
                            question.answer?.value &&
                            question.visible && (
                                <ApplyV2DataRow title={question.name}>{question.answer?.value}</ApplyV2DataRow>
                            )
                        )

                    case ApplyV2CustomFieldTypeConst.Integer: {
                        const value = question.answer?.value
                        const decimals = question.customField.properties?.decimals ?? 0

                        if (typeof value === 'number') {
                            const flag = getCurrencyById(question.customField.properties?.currencyId)
                            if (flag && value) {
                                return (
                                    question.visible && (
                                        <ApplyV2DataRow title={question.name}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <ApplyV2Picture
                                                    className={`${baseClass}__currency`}
                                                    fallbackSrc={`${window.location.protocol}//${window.location.hostname}/${flag?.img}`}
                                                    alt=""
                                                />
                                                <span>{value.toFixed(decimals)}</span>
                                            </div>
                                        </ApplyV2DataRow>
                                    )
                                )
                            }
                            return (
                                question.visible &&
                                !!value && (
                                    <ApplyV2DataRow title={question.name}>{value.toFixed(decimals)}</ApplyV2DataRow>
                                )
                            )
                        }
                        return (
                            !!value &&
                            question.visible && <ApplyV2DataRow title={question.name}>{String(value)}</ApplyV2DataRow>
                        )
                    }

                    case ApplyV2CustomFieldTypeConst.Date: {
                        const answerValue = question.answer?.value
                        const [from, to] = Array.isArray(answerValue)
                            ? (answerValue as (string | number | null | undefined)[])
                            : [answerValue]
                        return (
                            (from ?? to) &&
                            question.visible && (
                                <ApplyV2DataRow title={question.name}>
                                    {formatDateRange(from, to, {
                                        isShowPresent: false,
                                        dateFormat: 'D MMMM YYYY',
                                    })}
                                </ApplyV2DataRow>
                            )
                        )
                    }

                    default:
                        return
                }

            case 'attribute':
                if (!question.answer || !question.visible) {
                    return null
                }
                return (
                    <ApplyV2DataRow title={question.name}>
                        <div className={`${baseClass}__list`}>
                            {question.attribute.bool && (
                                <div>
                                    {question.answer?.value?.bool ? getTitle('128', 'Yes') : getTitle('129', 'No')}
                                </div>
                            )}
                            {question.attribute.checkbox && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {Object.values(question.answer?.value?.checkbox ?? []).map(({ id, rating }) => {
                                        const selected = (question.attribute?.checkboxes ?? []).find(
                                            (option) => option.id === id
                                        )?.name
                                        return (
                                            <div key={id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span>{selected}</span>
                                                {rating && rating > 0 && (
                                                    <span className={`${baseClass}__rating`}>
                                                        <span className={`${baseClass}__rating__star`}>★</span>
                                                        <span>{rating}</span>
                                                    </span>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                            {question.attribute.date && question.answer?.value.date && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {formatDateRange(question.answer?.value.date, null, {
                                        isShowPresent: false,
                                        dateFormat: 'D MMMM YYYY',
                                    })}
                                </div>
                            )}
                            {question.attribute.dropdown && question.answer?.value.dropdown && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {(() => {
                                        return (question.attribute.dropdowns ?? []).find(
                                            ({ id }) => id === question.answer?.value.dropdown
                                        )?.name
                                    })()}
                                </div>
                            )}
                            {question.attribute.rating && question.answer?.value.rating && (
                                <span className={`${baseClass}__rating`}>
                                    <span className={`${baseClass}__rating__star`}>★</span>
                                    {question.answer?.value?.rating}
                                </span>
                            )}
                            {question.attribute.shortText && question.answer?.value.shortText && (
                                <span>{question.answer?.value.shortText}</span>
                            )}
                            {question.attribute.text && question.answer?.value.text && (
                                <span>{question.answer?.value.text}</span>
                            )}
                        </div>
                    </ApplyV2DataRow>
                )

            default:
                return null
        }
    }

    return (
        <div className={`${baseClass}__item`}>
            {/*<ApplyV2DataRow title={question.name}>{renderAnswer()}</ApplyV2DataRow>*/}
            {renderAnswer()}
            {question.children && <ReviewQuestionsList list={question.children} />}
        </div>
    )
}

export default ApplyV2ReviewApplyQuestions
